import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { CheckCircle2, XCircle, PackageCheck, Landmark, RotateCcw } from "lucide-react";
import { angka } from "@/lib/format";

export default function Closing() {
  const { data: projects, reload } = useFetch("/projects");
  const [selId, setSelId] = useState(null);
  const [check, setCheck] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const selectProject = async (p) => {
    setSelId(p.id); setCheck(null);
    const r = await api.get(`/projects/${p.id}/closing-check`);
    setCheck(r.data);
  };

  const doClose = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/projects/${selId}/close`, { kind, reason });
      toast.success(`Closing ${kind === "operational" ? "Operasional" : "Finance"} berhasil`);
      const r = await api.get(`/projects/${selId}/closing-check`); setCheck(r.data); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal closing"); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    if (!reason) { toast.error("Alasan wajib diisi"); return; }
    setBusy(true);
    try { await api.post(`/projects/${selId}/reopen`, { reason }); toast.success("Proyek dibuka kembali"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Closing Proyek" subtitle="Pemeriksaan kelayakan closing operasional & finance" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-4 lg:col-span-1">
          <h3 className="font-semibold text-sm mb-3">Daftar Proyek</h3>
          <div className="space-y-1">
            {(projects || []).map((p) => (
              <button key={p.id} onClick={() => selectProject(p)} data-testid={`closing-project-${p.id}`}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selId === p.id ? "bg-[#9333EA] text-white" : "hover:bg-white/5"}`}>
                <div className="font-medium">{p.name}</div>
                <div className={`text-xs ${selId === p.id ? "text-blue-100" : "text-muted-foreground"}`}>{p.rental_status}</div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          {!check ? <p className="text-muted-foreground text-sm">Pilih proyek untuk memeriksa kelayakan closing.</p> : (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold">Status Kelayakan</h3>
                <StatusBadge status={check.status} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[["Saldo Material", angka(check.balance)], ["Dikirim", angka(check.delivered)], ["Kembali Baik", angka(check.returned_good)], ["Reject", angka(check.rejected)], ["Hilang", angka(check.lost)], ["Retur Belum Verifikasi", check.unverified_returns], ["Proforma Tertunda", check.pending_proforma], ["Draft Invoice", check.pending_draft_invoice], ["Invoice Belum Lunas", check.unpaid_invoices]].map(([k, v]) => (
                  <div key={k} className="rounded-md border p-3"><div className="text-xs text-muted-foreground">{k}</div><div className="font-semibold tabular-nums">{v}</div></div>
                ))}
              </div>
              <div className="space-y-2">
                <Req ok={check.operational_ready} label="Syarat Closing Operasional (saldo 0, retur terverifikasi, reject/hilang selesai)" />
                <Req ok={check.finance_ready} label="Syarat Closing Finance (operasional selesai, tidak ada proforma/draft invoice tertunda)" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Alasan / Catatan (wajib untuk buka kembali)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="closing-reason" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button disabled={!check.operational_ready || busy} onClick={() => doClose("operational")} data-testid="close-operational-btn"><PackageCheck className="h-4 w-4 mr-1" /> Closing Operasional</Button>
                <Button disabled={!check.finance_ready || busy} onClick={() => doClose("finance")} data-testid="close-finance-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]"><Landmark className="h-4 w-4 mr-1" /> Closing Finance</Button>
                <Button variant="outline" onClick={reopen} disabled={busy} data-testid="reopen-btn"><RotateCcw className="h-4 w-4 mr-1" /> Buka Kembali</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Req({ ok, label }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : <XCircle className="h-4 w-4 text-rose-500 mt-0.5" />}
      <span className={ok ? "text-emerald-700" : "text-muted-foreground"}>{label}</span>
    </div>
  );
}
